import dayjs from 'dayjs'
import 'dayjs/locale/fi'
import relativeTime from 'dayjs/plugin/relativeTime'
import calendar from 'dayjs/plugin/calendar'

dayjs.extend(relativeTime)
dayjs.extend(calendar)
dayjs.locale('fi')

/**
 * Format a date with both date and time
 * Example: "26.8.2024 14:30"
 */
export function formatDateTime(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format a date without time
 * Example: "26.8.2024"
 */
export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format a date with abbreviated month
 * Example: "26. elo 2024, 14:30"
 */
export function formatDateTimeLong(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format relative time from now
 * Example: "2 tuntia sitten", "eilen", "3 päivää sitten"
 */
export function formatRelativeTime(date: string | Date): string {
  return dayjs(date).fromNow()
}

/**
 * Format a short date
 * Example: "26.8."
 */
export function formatShortDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
  })
}

/**
 * Check if a date is overdue (past current time)
 */
export function isOverdue(date: string | Date): boolean {
  return new Date(date) < new Date()
}

/**
 * Check if a date is today
 */
export function isToday(date: string | Date): boolean {
  const d = new Date(date)
  const today = new Date()
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  )
}

/**
 * Format time only
 * Example: "14:30"
 */
export function formatTime(date: string | Date): string {
  return dayjs(date).format('HH:mm')
}

/**
 * Convert a Date or string to ISO date string (YYYY-MM-DD)
 * Useful for sending date values to the API
 * Example: new Date(2025, 0, 15) → "2025-01-15"
 */
export function toDateString(date: string | Date): string {
  return dayjs(date).format('YYYY-MM-DD')
}

/**
 * Format duration between two dates
 * Example: "2h 30min" or "45min"
 * Returns null if duration is negative (invalid data)
 */
export function formatDuration(
  startDate: string | Date,
  endDate?: string | Date,
): string | null {
  const start = dayjs(startDate)
  const end = endDate ? dayjs(endDate) : dayjs()
  const diff = end.diff(start)

  if (diff < 0) return null

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 0) {
    return `${hours}h ${minutes}min`
  }
  return `${minutes}min`
}

/**
 * Format a date using calendar format with Finnish translations
 * Example: "Tänään", "Huomenna", "Eilen", "26.8."
 * @param date - Date to format
 * @param type - Whether the date is in the future (upcoming) or past
 */
export function formatCalendar(
  date: string | Date,
  type: 'upcoming' | 'past' = 'upcoming',
): string {
  const d = dayjs(date)

  if (type === 'upcoming') {
    return d.calendar(null, {
      sameDay: '[Tänään]',
      nextDay: '[Huomenna]',
      nextWeek: 'D.M.',
      sameElse: 'D.M.',
    })
  }

  return d.calendar(null, {
    sameDay: '[Tänään]',
    lastDay: '[Eilen]',
    lastWeek: 'D.M.',
    sameElse: 'D.M.',
  })
}
